import BreadCrumb from 'Components/Common/BreadCrumb';
import React, { useState, useEffect } from 'react';
import { Button, Container, Spinner } from 'reactstrap';
import Information from './Information';
import './ListingDetail.css';
import ReviewListing from './ReviewListing';
import Action from './Action';
import InspectionDetails from './InspectionDetails';
import CompanyInspection from './CompanyInspection';
import { useSelector } from 'react-redux';
import { RootState } from 'slices/store';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { builderPropertyListingDetails, updateStatusPropertyUnitById, getEarnestDepositById, propertyUnitInspectionDetails } from 'endpoints/builder/builderPropertyListingDetails';
import { formatPropertyType, formatFileSize, formatDateTime  } from 'lib/utils/formatters';
import SecondaryInspectionDetailCard from 'pages/Users/UserDetailPage/Components/SecondaryInspectionDetailCard';
import EarnestDeposit from './EarnestDeposit';
import FinalDisclosureModal from './FinalDisclosureModal';
import { useLocation, useSearchParams } from 'react-router-dom';
import { detectNavigationSource, setNavigationContext } from '../../lib/utils/navigationUtils';
import PropertyRejectionReasonCard from './PropertyRejectionReasonCard';

/**
 * Navigation Pattern Implementation:
 * 
 * To navigate to this page from different sources:
 * 
 * 1. From Builder Detail page:
 *    navigate('/listing-detail?source=builder')
 * 
 * 2. From Properties page:
 *    navigate('/listing-detail?source=properties')
 * 
 * This component will automatically:
 * - Set appropriate breadcrumbs
 * - Update sidebar active state
 * - Set correct page title
 * - Store navigation context
 */

// import FinalDisclosureModal from './FinalDisclosureModal';


const ListingDetail = () => {
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const [primaryInspectionId, setPrimaryInspectionId] = useState<string>('');
    const [secondaryInspectionId, setSecondaryInspectionId] = useState<string>('');
    const [earnestDepositId, setEarnestDepositId] = useState<string>('');

    // Navigation source detection
    const [navigationConfig, setNavigationConfig] = useState(detectNavigationSource(searchParams, location.pathname));

    // info states
    const [infoData, setInfoData] = useState({
        propertyUnitId: '',
        companyName: '',
        propertyUnitAddress: '',
        propertyCategory: '',
        propertyUnitType: '',
        propertyUnitStatus: '',
    });
    console.log('infoData', infoData)
    // property rejection reason state
    const [propertyRejectionReason, setPropertyRejectionReason] = useState<string>('');
    // company inspection state
    const [companyInspectionData, setCompanyInspectionData] = useState(null);
    const [companyInspectionStatus, setCompanyInspectionStatus] = useState<string>('');
    // secondary inspection state
    const [secondaryInspectionData, setSecondaryInspectionData] = useState(null);
    const [secondaryInspectionStatus, setSecondaryInspectionStatus] = useState<string>('');
    // earnest deposit state
    const [earnestDepositData, setEarnestDepositData] = useState(null);
    // contract data with proper typing
    const [contractData, setContractData] = useState<{
        id?: string;
        file_name?: string;
        file_type?: string;
        file_category?: string;
        file_size?: number;
        mimetype?: string;
        file_url?: string;
        associated_entity_id?: string;
        envelopeId?: string | null;
        createdAt?: string;
        updatedAt?: string;
        [key: string]: any;
    }>({});

    // Final Disclosure Modal state
    const [finalDisclosureModalOpen, setFinalDisclosureModalOpen] = useState(false);

    // Handle Live Property button click
    const handleLiveProperty = () => {
        if (!selectedPropertyUnitId) {
            toast.error('Property Unit ID is required');
            return;
        }

        updatePropertyStatusMutation.mutate({
            status: 'public'
        });
    };

    // Handle contract file download
    const handleDownloadContract = () => {
        if (contractData?.file_url) {
            window.open(contractData.file_url, '_blank');
        }
    };


    const selectedPropertyUnitId = useSelector((state: RootState) => state.Builder.selectedPropertyUnitId);
    const selectedBuilderId = useSelector((state: RootState) => state.Builder.selectedBuilderId);

    // Update Property Status Mutation
    const updatePropertyStatusMutation = useMutation({
        mutationFn: (data: { status: string, rejection_reason?: string }) =>
            updateStatusPropertyUnitById(selectedPropertyUnitId, data),
        onSuccess: (response) => {
            if (response?.status_code === 200) {
                toast.success(response?.response || 'Property status updated successfully');
                // Update the local state with new status
                if (response?.data?.status) {
                    setInfoData(prev => ({
                        ...prev,
                        propertyUnitStatus: response.data.status
                    }));
                }
            } else {
                toast.error('Failed to update property status');
            }
        },
        onError: (error: any) => {
            console.error('Error updating property status:', error);
            toast.error(error?.response?.data?.message || 'Failed to update property status');
        }
    });

    // Detect navigation source and set page title
    useEffect(() => {
        const config = detectNavigationSource(searchParams, location.pathname);

        setNavigationConfig(config);
        setNavigationContext(config);

        // Dispatch custom event to notify sidebar
        setTimeout(() => {
            const event = new CustomEvent('sidebarUpdate');
            window.dispatchEvent(event);
        }, 50);
    }, [location, searchParams]);

    // Dynamic breadcrumb configuration
    const getBreadcrumbConfig = () => {
        return {
            title: navigationConfig.title,
            pageTitles: navigationConfig.breadcrumbs
        };
    };

    const { data: propertyDetails, isLoading, isPending, isFetching, isError } = useQuery({
        queryKey: ['builderPropertyListingDetails', selectedPropertyUnitId, selectedBuilderId],
        queryFn: () => builderPropertyListingDetails({
            propertyUnitId: selectedPropertyUnitId,
            builderId: selectedBuilderId
        }),
        enabled: !!selectedPropertyUnitId && !!selectedBuilderId,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: true,
        retry: 3,
    });

    // Primary Inspection Query
    const { data: primaryInspectionDetails, isLoading: isPrimaryInspectionLoading, refetch: refetchPrimaryInspectionDetails, isRefetching: isPrimaryInspectionRefetching, isError: isPrimaryInspectionError } = useQuery({
        queryKey: ['propertyUnitInspectionDetails', primaryInspectionId],
        queryFn: () => propertyUnitInspectionDetails(primaryInspectionId),
        enabled: !!primaryInspectionId,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: true,
        retry: 3,
    });

    // Secondary Inspection Query
    const { data: secondaryInspectionDetails, isLoading: isSecondaryInspectionLoading, refetch: refetchSecondaryInspectionDetails, isRefetching: isSecondaryInspectionRefetching, isError: isSecondaryInspectionError } = useQuery({
        queryKey: ['propertyUnitSecondaryInspectionDetails', secondaryInspectionId],
        queryFn: () => propertyUnitInspectionDetails(secondaryInspectionId),
        enabled: !!secondaryInspectionId,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: true,
        retry: 3,
    });

    const { data: earnestDepositDetails, isLoading: isEarnestDepositLoading, refetch: refetchEarnestDepositDetails, isRefetching: isEarnestDepositRefetching, isError: isEarnestDepositError } = useQuery({
        queryKey: ['propertyUnitEarnestDepositDetails', earnestDepositId],
        queryFn: () => getEarnestDepositById(earnestDepositId),
        enabled: !!earnestDepositId ,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: true,
        retry: 3,
    });

    useEffect(() => {
        if (propertyDetails?.data) {
            const data = propertyDetails.data;
            setInfoData({
                propertyUnitId: data?.id || 'N/A',
                companyName: data?.builder?.company_name || 'N/A',
                propertyUnitAddress: data?.property?.property_address || 'N/A',
                propertyCategory: data?.property?.property_category ? formatPropertyType(data.property.property_category) : 'N/A',
                propertyUnitType: data?.property?.property_type ? formatPropertyType(data.property.property_type) : 'N/A',
                propertyUnitStatus: data?.status ? data?.status : 'N/A',
            });
            setPropertyRejectionReason(data?.rejection_reason || '');
            setPrimaryInspectionId(data?.primaryInspection?.id || '');
            setSecondaryInspectionId(data?.secondaryInspection?.id || '');
            setEarnestDepositId(data?.earnestDepositPayment?.id || '');
            setContractData(data?.agreementFile || {});
        }
    }, [propertyDetails]);

    // Handle Primary Inspection Data
    useEffect(() => {
        if (primaryInspectionDetails?.data) {
            const data = primaryInspectionDetails.data;
            setCompanyInspectionData(data || {});
            setCompanyInspectionStatus(data?.status || '');
        } else {
            setCompanyInspectionData(null);
        }
    }, [primaryInspectionDetails]);

    // Handle Secondary Inspection Data
    useEffect(() => {
        if (secondaryInspectionDetails?.data) {
            const data = secondaryInspectionDetails.data;
            setSecondaryInspectionData(data || {});
            setSecondaryInspectionStatus(data?.status || '');
        } else {
            setSecondaryInspectionData(null);
        }
    }, [secondaryInspectionDetails]);

    // handle earnest deposit data
    useEffect(() => {
        if (earnestDepositDetails?.data) {
            const data = earnestDepositDetails.data;
            setEarnestDepositData(data || {});
            // setEarnestDepositStatus(data?.status || '');
        } else {
            setEarnestDepositData(null);
        }
    }, [earnestDepositDetails]);

    if ((isPending || isFetching) && !propertyDetails) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <div className="text-center">
                    <div className="text-danger mb-3">
                        <i className="ri-error-warning-line" style={{ fontSize: '3rem' }}></i>
                    </div>
                    <h5 className="text-danger">Failed to load builder data</h5>
                    <p className="text-muted">Please refresh the page to try again.</p>
                </div>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid >
                    <div className="sticky-header-wrapper">
                        <BreadCrumb
                            title={getBreadcrumbConfig().title}
                            pageTitles={getBreadcrumbConfig().pageTitles}
                        />
                        <Information
                            infoData={infoData}
                            isLoading={isLoading}
                        />
                    </div>
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', paddingBottom: '20px' }}>
                        <div className="listing-detail-left">
                            <ReviewListing
                                propertyDetails={propertyDetails?.data}
                                isLoading={isLoading}
                            />
                        </div>
                        <div className="listing-detail-right">
                                <Button
                                    color="success"
                                    className="rounded-0 mt-2 mb-3 w-100"
                                    onClick={handleLiveProperty}
                                    disabled={updatePropertyStatusMutation.isPending || (infoData?.propertyUnitStatus !== 'pending' && infoData?.propertyUnitStatus !== 'rejected') }
                                >
                                    {updatePropertyStatusMutation.isPending ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Processing...
                                        </>
                                    ) : (
                                        'Make Public Property'
                                    )}
                                </Button>
                            {infoData?.propertyUnitStatus === 'rejected' && propertyRejectionReason && <PropertyRejectionReasonCard rejectionReason={propertyRejectionReason} />}
                            {infoData?.propertyUnitStatus === 'pending' && <Action inspectionId={primaryInspectionId} companyInspectionStatus={companyInspectionStatus} isInspectionLoading={isPrimaryInspectionLoading} setInspectionId={setPrimaryInspectionId} setPropertyUnitStatus={(status: string) => setInfoData(prev => ({ ...prev, propertyUnitStatus: status }))} propertyUnitId={infoData.propertyUnitId} setPropertyRejectionReason={setPropertyRejectionReason}/>}

                            {/* Primary Inspection Section */}
                            {isPrimaryInspectionLoading || isPrimaryInspectionRefetching ? (
                                <div className="text-center py-5">
                                    <Spinner color="primary" />
                                    <p className="mt-2 text-muted">Loading Company Inspection...</p>
                                </div>
                            ) : isPrimaryInspectionError ? (
                                <div className="text-center py-5">
                                    <div className="text-danger mb-3">
                                        <i className="ri-error-warning-line" style={{ fontSize: '3rem' }}></i>
                                    </div>
                                    <h5 className="text-danger">Failed to load Company inspection</h5>
                                    <p className="text-muted">Please check your connection and try again.</p>
                                    <Button color="primary" onClick={() => refetchPrimaryInspectionDetails()}>
                                        <i className="ri-refresh-line me-1"></i>
                                        Retry
                                    </Button>
                                </div>
                            ) : companyInspectionData ? (
                                <CompanyInspection companyInspectionData={companyInspectionData} setInspectionId={setPrimaryInspectionId} inspectionId={primaryInspectionId} />
                            ) : !companyInspectionData && infoData?.propertyUnitStatus === 'pending' ? (
                                <InspectionDetails />
                            ) : null}

                            {isEarnestDepositLoading || isEarnestDepositRefetching ? (
                                <div className="text-center py-4 mt-3">
                                    <Spinner color="primary" />
                                    <p className="mt-2 text-muted">Loading Earnest Deposit...</p>
                                </div>
                            ) : isEarnestDepositError ? (
                                <div className="text-center py-4 mt-3">
                                    <div className="text-danger mb-3">
                                        <i className="ri-error-warning-line" style={{ fontSize: '2rem' }}></i>
                                    </div>
                                    <h6 className="text-danger">Failed to load earnest deposit</h6>
                                    <p className="text-muted">Please check your connection and try again.</p>
                                    <Button color="secondary" size="sm" onClick={() => refetchEarnestDepositDetails()}>
                                        <i className="ri-refresh-line me-1"></i>
                                        Retry
                                    </Button>
                                </div>
                            ) : earnestDepositData ? (
                                <EarnestDeposit earnestDepositData={earnestDepositData} infoData={infoData} />
                            ) : (
                                null
                            )}

                            {/* Contract/Agreement File Section */}
                            {contractData?.file_url && (
                                <div className="card mt-3">
                                    <div className="card-header">
                                        <h6 className="mb-0">{contractData?.file_category === 'rental_agreement'? 'Rental Agreement' : 'Property Agreement'}</h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="d-flex align-items-center mb-2">
                                            <div className="rounded-circle bg-light d-flex justify-content-center align-items-center me-3" style={{ width: '48px', height: '48px' }}>
                                                <i className="ri-file-text-line" style={{ fontSize: '2rem', color: '#fa7575ff' }}></i>
                                            </div>
                                            <div className="flex-grow-1">
                                                <div>{contractData?.file_category === 'rental_agreement'? 'Rental Agreement' : 'Property Agreement'}</div>
                                                {contractData?.file_size && (
                                                    <div>{formatFileSize(contractData.file_size)}</div>
                                                )}
                                            </div>
                                            <div
                                                onClick={handleDownloadContract}
                                                style={{ cursor: 'pointer' }}
                                                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                                                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                            >
                                                <i className="ri-download-cloud-line" style={{ fontSize: '1.5rem', color: '#222', cursor: 'pointer' }}></i>
                                            </div>
                                        </div>
                                        
                                        {contractData?.createdAt && (
                                            <div className="d-flex align-items-center justify-content-center mb-3 p-2"
                                                style={{
                                                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                                    border: '1px solid #dee2e6',
                                                    borderRadius: '8px',
                                                    fontSize: '0.875rem'
                                                }}>
                                                <i className="ri-calendar-line me-2" style={{ color: '#6c757d', fontSize: '1rem' }}></i>
                                                <span style={{ color: '#495057', fontWeight: '500' }}>
                                                    File Created: {formatDateTime(contractData.createdAt)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Secondary Inspection Section */}
                            {secondaryInspectionId && (
                                <>
                                    {isSecondaryInspectionLoading || isSecondaryInspectionRefetching ? (
                                        <div className="text-center py-4 mt-3">
                                            <Spinner color="primary" />
                                            <p className="mt-2 text-muted">Loading Secondary Inspection...</p>
                                        </div>
                                    ) : isSecondaryInspectionError ? (
                                        <div className="text-center py-4 mt-3">
                                            <div className="text-danger mb-3">
                                                <i className="ri-error-warning-line" style={{ fontSize: '2rem' }}></i>
                                            </div>
                                            <h6 className="text-danger">Failed to load secondary inspection</h6>
                                            <p className="text-muted">Please check your connection and try again.</p>
                                            <Button color="secondary" size="sm" onClick={() => refetchSecondaryInspectionDetails()}>
                                                <i className="ri-refresh-line me-1"></i>
                                                Retry
                                            </Button>
                                        </div>
                                    ) : secondaryInspectionData ? (
                                        <SecondaryInspectionDetailCard secondaryInspectionData={secondaryInspectionData} setSecondaryInspectionId={setSecondaryInspectionId} secondaryInspectionId={secondaryInspectionId} />
                                    ) : (
                                        null
                                    )}
                                </>
                            )}

                            {( infoData?.propertyCategory === 'Sell'&& (infoData?.propertyUnitStatus === 'earnest_paid')) && 
                             secondaryInspectionStatus === 'approved' && (
                                <Button color="success" className="rounded-0 mt-3 w-100" onClick={() => setFinalDisclosureModalOpen(true)}>
                                    Final Disclosure
                                </Button>
                            )}

                        </div>
                    </div>
                </Container>
            </div>

            {/* Final Disclosure Modal */}
            <FinalDisclosureModal
                isOpen={finalDisclosureModalOpen}
                toggle={() => setFinalDisclosureModalOpen(false)}
                earnestDepositData={earnestDepositData}
                onUpdate={(updatedData: any) => {
                    setEarnestDepositData(updatedData);
                    // setFinalDisclosureModalOpen(false);
                }}
            />
        </React.Fragment>
    );
}

export default ListingDetail;   
